import { create } from "zustand";

import {
  authApi,
  type LoginInput,
  type MfaChallengeResponse,
} from "@/client/features/auth/auth-api";
import { ApiError } from "@/client/lib/api";
import type {
  AppFeatures,
  ImpersonationInfo,
  SessionResponse,
  SessionUser,
  UserKind,
} from "@/shared/types";

type AuthStatus = "loading" | "authenticated" | "anonymous";

const NO_FEATURES: AppFeatures = {
  tickets: false,
  nodes: false,
  pxe: false,
  publicPorts: false,
  sshKeys: false,
  networkAccess: false,
  bmc: false,
  sol: false,
  storage: false,
};

interface AuthState {
  status: AuthStatus;
  user: SessionUser | null;
  features: AppFeatures;
  /** Set while viewing the portal as a tenant (read-only staff session). */
  impersonation: ImpersonationInfo | null;
  /**
   * True when the customer's tenant is deactivated (PE-991): read access is
   * kept, but self-service write actions are disabled. Drives the shell banner
   * and disabled action buttons. Always false for staff.
   */
  deactivated: boolean;
  /** Fetch the current session once on app load. Failures => anonymous. */
  loadSession: () => Promise<void>;
  login: (
    input: LoginInput,
  ) => Promise<
    { kind: "authenticated"; user: SessionUser } | { kind: "mfa"; challenge: MfaChallengeResponse }
  >;
  completeSession: (session: SessionResponse) => SessionUser;
  logout: () => Promise<void>;
  setUser: (user: SessionUser, features?: AppFeatures, deactivated?: boolean) => void;
  /**
   * DEV-ONLY escape hatch: seed a fake session so both authenticated shells are
   * navigable before the auth backend (PE-860) exists. No-op in production.
   */
  mockSignIn: (kind: UserKind) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  status: "loading",
  user: null,
  features: NO_FEATURES,
  impersonation: null,
  deactivated: false,

  loadSession: async () => {
    try {
      const { user, features, impersonation, deactivated } = await authApi.session();
      set({
        user,
        features,
        impersonation: impersonation ?? null,
        deactivated: deactivated ?? false,
        status: "authenticated",
      });
    } catch (err) {
      // 401/404 simply means "not signed in". Surface unexpected (network)
      // failures in the console but still drop to the public surface.
      if (!(err instanceof ApiError)) {
        console.error("Session check failed", err);
      }
      set({
        user: null,
        features: NO_FEATURES,
        impersonation: null,
        deactivated: false,
        status: "anonymous",
      });
    }
  },

  login: async (input) => {
    const response = await authApi.login(input);
    if ("mfaRequired" in response) {
      set({ status: "anonymous", user: null });
      return { kind: "mfa", challenge: response };
    }
    const { user, features, deactivated } = response;
    set({
      user,
      features,
      impersonation: null,
      deactivated: deactivated ?? false,
      status: "authenticated",
    });
    return { kind: "authenticated", user };
  },

  completeSession: (session) => {
    set({
      user: session.user,
      features: session.features,
      impersonation: session.impersonation ?? null,
      deactivated: session.deactivated ?? false,
      status: "authenticated",
    });
    return session.user;
  },

  logout: async () => {
    try {
      await authApi.logout();
    } catch {
      // Clearing local state is what matters; ignore transport errors so the
      // user is always signed out client-side.
    }
    set({
      user: null,
      features: NO_FEATURES,
      impersonation: null,
      deactivated: false,
      status: "anonymous",
    });
  },

  setUser: (user, features, deactivated) =>
    set({
      user,
      status: "authenticated",
      ...(features ? { features } : {}),
      // A member accepting an invite into an already-deactivated tenant must
      // land in the deactivated state (banner + disabled actions), not the
      // default active one, without waiting for a full session reload (PE-991).
      ...(deactivated === undefined ? {} : { deactivated }),
    }),

  mockSignIn: (kind) => {
    if (!import.meta.env.DEV) {
      return;
    }
    // DEV shells exercise every feature; enable all so the nav/pages are reachable.
    set({
      status: "authenticated",
      user: mockUser(kind),
      features: {
        tickets: true,
        nodes: true,
        pxe: true,
        publicPorts: true,
        sshKeys: true,
        networkAccess: true,
        bmc: true,
        sol: true,
        storage: true,
      },
    });
  },
}));

/** Where a given principal lands after authenticating. */
export function homePathForUser(user: SessionUser): string {
  return user.kind === "staff" ? "/admin" : "/";
}

function mockUser(kind: UserKind): SessionUser {
  if (kind === "staff") {
    return {
      id: "00000000-0000-0000-0000-0000000000s7",
      email: "staff@mantledc.ai",
      name: "Dev Staff",
      kind: "staff",
      tenantId: null,
      tenantName: null,
      tenantRole: null,
      staffRole: "admin",
      mfaEnabled: false,
    };
  }
  return {
    id: "00000000-0000-0000-0000-0000000000c1",
    email: "customer@example.com",
    name: "Dev Customer",
    kind: "customer",
    tenantId: "00000000-0000-0000-0000-00000000aaaa",
    tenantName: "Dev Tenant",
    tenantRole: "tenant_admin",
    staffRole: null,
    mfaEnabled: false,
  };
}
